import { Wand2, Sparkles } from "lucide-react";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  SKILL_FOCUS_OPTIONS,
  SKILL_FOCUS_DESCRIPTORS,
  SKILL_FOCUS_AUTO,
} from "../lib/options";

/**
 * SkillFocusPicker — Tier 2 #4 (Feb 2026).
 *
 * Shared between the main Generator, the Skills Generator, and the
 * homepage Free Lesson form. The transferable skill is the foundation
 * of every lesson, so this picker:
 *
 *   1. Is REQUIRED on every form (callers gate Generate on a value).
 *   2. Teaches as it asks — each option shows a one-line descriptor
 *      of what choosing that skill does to the generated lesson.
 *   3. Offers a top "Let Atomic Edu choose" sentinel so a rushed
 *      teacher doesn't get blocked; the backend picks the best-fit
 *      skill for grade + subject and names it clearly in the output.
 *   4. Shows a confirmation line under the field once a skill is
 *      picked: "This lesson will weave [skill] through the task
 *      design, look-fors, and reflection."
 */
export const SkillFocusPicker = ({
  value,
  onChange,
  testid = "select-skill-focus",
  label = "Skill Focus",
  triggerClassName = "",
  required = true,
  showConfirmation = true,
}) => {
  const isAuto = value === SKILL_FOCUS_AUTO;
  const hasValue = !!value;
  const confirmation = isAuto
    ? "Atomic Edu will pick the best-fit transferable skill for your grade + subject and name it in the output."
    : hasValue
    ? `This lesson will weave ${value} through the task design, look-fors, and reflection.`
    : null;
  return (
    <div className="space-y-2" data-testid={`${testid}-wrap`}>
      <Label
        htmlFor={testid}
        className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]"
      >
        {label} {required && <span className="text-[#1c2d3d]">*</span>}
      </Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger
          id={testid}
          data-testid={testid}
          className={
            "bg-white border-[#4a6984]/15 text-[#4a6984] data-[placeholder]:text-[#4a6984]/60 " +
            triggerClassName
          }
        >
          {/* Render a compact single-line display in the trigger so the
              full skill name doesn't get clipped by the dropdown's
              multi-line descriptor layout. The descriptor stays inside
              the dropdown only. */}
          {hasValue ? (
            <span className="flex items-center gap-2 min-w-0 w-full">
              {isAuto && (
                <Sparkles className="h-4 w-4 text-[#1c2d3d] shrink-0" />
              )}
              <span className="truncate font-semibold text-[#1c2d3d]">
                {value}
              </span>
            </span>
          ) : (
            <SelectValue placeholder="Pick a transferable skill (or let us choose)" />
          )}
        </SelectTrigger>
        <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984] max-h-[420px]">
          <SelectItem
            value={SKILL_FOCUS_AUTO}
            data-testid={`${testid}-option-auto`}
            className="text-[#1c2d3d] font-medium focus:bg-[#96ded1]/30 focus:text-[#1c2d3d] cursor-pointer"
          >
            <div className="flex items-start gap-2 py-1">
              <Sparkles className="h-4 w-4 text-[#1c2d3d] mt-0.5 shrink-0" />
              <div>
                <p className="font-extrabold text-sm leading-tight">
                  Let Atomic Edu choose
                </p>
                <p className="text-xs text-[#4a6984]/70 mt-0.5 leading-snug">
                  Best-fit skill for your grade + subject, named clearly in the output.
                </p>
              </div>
            </div>
          </SelectItem>
          <div className="my-1 mx-2 h-px bg-[#4a6984]/10" aria-hidden="true" />
          {SKILL_FOCUS_OPTIONS.map((opt) => (
            <SelectItem
              key={opt}
              value={opt}
              data-testid={`${testid}-option-${opt
                .replace(/[^a-z0-9]+/gi, "-")
                .toLowerCase()
                .replace(/^-+|-+$/g, "")}`}
              className="text-[#4a6984] font-medium focus:bg-[#f4f5f7] focus:text-[#4a6984] cursor-pointer"
            >
              <div className="py-1 max-w-[420px]">
                <p className="font-semibold text-sm leading-tight">{opt}</p>
                {SKILL_FOCUS_DESCRIPTORS[opt] && (
                  <p className="text-xs text-[#4a6984]/70 mt-0.5 leading-snug">
                    {SKILL_FOCUS_DESCRIPTORS[opt]}
                  </p>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {showConfirmation && confirmation && (
        <p
          data-testid={`${testid}-confirmation`}
          className="text-xs text-[#1c2d3d]/85 leading-snug inline-flex items-start gap-1.5 pt-0.5"
        >
          <Wand2 className="h-3 w-3 text-[#1c2d3d] mt-0.5 shrink-0" />
          <span>{confirmation}</span>
        </p>
      )}
    </div>
  );
};

export default SkillFocusPicker;
